import { useMemo } from 'react';
import { Paper, Box } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import {
  premiumTooltip,
  horizontalGrid,
  indianValueAxis,
  indianCategoryAxis,
  stackedBarTooltipFormatter,
  defaultAnimation,
  STATUS_COLORS,
} from './echartsConfig';
import { reportChartCanvasSx, reportChartPaperSx } from './reportChartLayout';
import ReportCardHeader, { ReportInsightBanner } from './ReportCardHeader';

/** Map dataset label (Confirmed / Tentative / …) to shared report status colors. */
function barColorFromDatasetLabel(label, explicitBackground) {
  const key = String(label || '')
    .toLowerCase()
    .trim();
  if (key.includes('confirm')) {
    return STATUS_COLORS.confirmed;
  }
  if (key.includes('tent')) {
    return STATUS_COLORS.tentative;
  }
  if (key.includes('wait')) {
    return STATUS_COLORS.waitlisted;
  }
  if (key.includes('cancel')) {
    return STATUS_COLORS.cancelled;
  }
  if (key.includes('rfq')) {
    return STATUS_COLORS.rfq;
  }
  if (key.includes('actual')) {
    return STATUS_COLORS.actualized;
  }
  if (key.includes('assign')) {
    return STATUS_COLORS.assigned;
  }
  return explicitBackground || '#90A4AE';
}

const HorizontalBarChart = ({ data, name, handleExport, handleEnlarge, showButton = true, embeddedInDialog = false }) => {
  const option = useMemo(() => {
    const labels = data?.labels || [];
    const datasets = data?.datasets || [];

    const echartsSeries = datasets.map(ds => ({
      name: ds.label || name,
      type: 'bar',
      stack: 'total',
      data: (ds.data || []).map(v => Number(v) || 0),
      barWidth: '55%',
      itemStyle: {
        color: barColorFromDatasetLabel(ds.label, ds.backgroundColor),
        borderRadius: 0,
      },
    }));

    return {
      ...defaultAnimation,
      tooltip: {
        ...premiumTooltip,
        formatter: stackedBarTooltipFormatter,
      },
      legend: { show: false },
      grid: horizontalGrid,
      xAxis: {
        ...indianValueAxis,
      },
      yAxis: {
        ...indianCategoryAxis,
        data: labels,
        axisLabel: {
          ...indianCategoryAxis.axisLabel,
          width: 100,
          overflow: 'truncate',
        },
      },
      series: echartsSeries,
    };
  }, [data, name]);

  const paperSx = embeddedInDialog
    ? {
        ...reportChartPaperSx,
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'none',
        border: 'none',
        px: 0,
        py: 0,
      }
    : reportChartPaperSx;

  const canvasSx = embeddedInDialog
    ? {
        flex: '1 1 auto',
        minHeight: 360,
        height: '100%',
        minWidth: 0,
        position: 'relative',
      }
    : reportChartCanvasSx;

  return (
    <Paper sx={paperSx}>
      {!embeddedInDialog && (
        <ReportCardHeader
          title={name}
          insight={data?.infoText || 'Horizontal stacked breakdown'}
          handleExport={handleExport}
          handleEnlarge={handleEnlarge}
          showButton={showButton}
        />
      )}
      <ReportInsightBanner text={data?.insightText} style={data?.insightStyle} />
      <Box sx={canvasSx}>
        <ReactECharts option={option} style={{ width: '100%', height: '100%' }} notMerge />
      </Box>
    </Paper>
  );
};

export default HorizontalBarChart;
